<template>
    <Configurator url="/colocation/confirm">
        <img
            src="/img/icons/23m/colocation.svg"
            alt="Colocation"
            class="mx-auto h-24 w-36"
        />
        <Columns>
            <Column>
                <ColumnTitle>{{ $t('configuratorColocation.base') }}</ColumnTitle>
                <template
                    v-for="(racks, groupName) in assortment.racksGrouped"
                    :key="groupName"
                >
                    <CategoryTitle>{{ groupName }}</CategoryTitle>
                    <div
                        v-for="rack in racks"
                        :key="rack.id"
                    >
                        <RadioBox
                            :id="rack.id"
                            v-model="fields.rackId"
                            :badges="rack.badges"
                            :title="rack.name"
                            :description="$t('configuratorColocation.mbsIncluded')"
                            :properties="rack.feed_hint"
                            name="rack"
                        />

                        <Range
                            v-if="rack.max_quantity"
                            v-model="fields.rackQuantity"
                            name="rackQuantity"
                            :disabled="String(fields.rackId) !== String(rack.id)"
                            :min="1"
                            :max="rack.max_quantity"
                        />
                    </div>
                </template>
            </Column>

            <Column>
                <ColumnTitle>{{ $t('configuratorColocation.power') }}</ColumnTitle>
                <CategoryTitle>Feeds</CategoryTitle>
                <Radios>
                    <template
                        v-for="feed in selection.rack.feeds"
                        :key="feed.id"
                    >
                        <Radio
                            :id="feed.id"
                            v-model="fields.feedId"
                            :label="feed.name"
                            name="feed"
                        />
                    </template>
                </Radios>
                <div>
                    <hint>{{ $t('configuratorColocation.powerSupply') }}</hint>
                    <hint
                        >{{
                            $t('configuratorColocation.fuse', {
                                fuseProtection: fuseProtection,
                            })
                        }}
                    </hint>
                </div>
                <div>
                    <CategoryTitle>
                        {{ $t('configuratorColocation.electricalPower') }}
                    </CategoryTitle>
                    <div class="space-y-3">
                        <QuantityWithButtons
                            v-model="fields.power"
                            name="power"
                            :min="powerMin"
                            :max="selection.rack.power.watts_max"
                            @decrease="decreasePower"
                            @increase="increasePower"
                        />
                        <hint data-spec-class="price-per-kwh"
                            >{{
                                $t('configuratorColocation.eachKwh', {
                                    price: selection.rack.power.price_per_kwh,
                                })
                            }}
                        </hint>
                    </div>
                </div>
            </Column>
            <Column>
                <NetworkConfigurationFieldset
                    :bandwidth-hint="$t('configuratorGeneral.includedBandwidthHint.colocation')"
                    :uplinks="selection.rack.uplinks"
                    :volumes="assortment.volumes"
                    :fields="fields"
                    :selection="selection"
                    @update:fields="Object.assign(fields, $event)"
                />
                <IpConfigurationFieldset
                    :assortment="assortment"
                    :fields="fields"
                    :selection="selection"
                    :can-select-ip-addresses="selection.rack.can_select_ip_addresses"
                    @update:fields="Object.assign(fields, $event)"
                />
            </Column>

            <Column>
                <ColumnTitle>{{ $t('configuratorGeneral.price') }}</ColumnTitle>
                <PriceBanner
                    :price="monthlyPrice"
                    :setup-price="setupPrice"
                />
                <Price
                    :price="monthlyPrice"
                    :setup_price="setupPrice"
                />

                <div>
                    <hint>{{ $t('configuratorColocation.provision') }}</hint>
                    <hint>{{ $t('configuratorColocation.contractTerm') }}</hint>
                    <hint>{{ $t('configuratorColocation.noticePeriod') }}</hint>
                </div>
                <Confirm>{{ $t('configuratorGeneral.continue') }}</Confirm>
            </Column>
        </Columns>
    </Configurator>
</template>

<script setup>
import Columns from './Configurator/Columns.vue';
import Column from './Configurator/Column.vue';
import Confirm from './Configurator/Confirm.vue';
import ColumnTitle from './Configurator/ColumnTitle.vue';
import Price from './Configurator/Price.vue';
import PriceBanner from './Configurator/PriceBanner.vue';
import QuantityWithButtons from './Configurator/QuantityWithButtons.vue';
import Radio from './Configurator/Radio.vue';
import Configurator from './Configurator/Configurator.vue';
import CategoryTitle from './Configurator/CategoryTitle.vue';
import Radios from './Configurator/Radios.vue';
import RadioBox from './Configurator/RadioBox.vue';
import Range from './Configurator/Range.vue';
import Hint from './Configurator/Hint.vue';
import { useAssortmentColocation } from '../composables/assortmentColocation';
import IpConfigurationFieldset from './IpConfigurationFieldset.vue';
import NetworkConfigurationFieldset from './NetworkConfigurationFieldset.vue';

const props = defineProps({
    assortmentContent: { type: String, default: '' },
    assortmentNetworkContent: { type: String, default: '' },
    assortmentIpContent: { type: String, default: '' },
});

const { fields, assortment, monthlyPrice, setupPrice, selection, powerMin, fuseProtection } =
    useAssortmentColocation(
        props.assortmentContent,
        props.assortmentNetworkContent,
        props.assortmentIpContent
    );

const increasePower = () => {
    fields.power += selection.value.rack.power.watts_step;
};
const decreasePower = () => {
    fields.power -= selection.value.rack.power.watts_step;
};
</script>
